import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import SelectableGoal from "./SelectableGoal";
import { mainGoalsListNew } from "../../../constants/GoalsMap";
import Tooltip from "../../materialCommitment/tooltip/Tooltip";
import translate from "../../reusable/translate";
import commitmentSlice from "../../../state/slices/commitmentSlice";
import validatorSlice from "../../../state/slices/validatorSlice";

const AgendaContainer = ({ fetchedCommitment, mainGoal }) => {
  const { commitment, language } = useSelector(state => state);
  const dispatch = useDispatch();
  const { setCategoryIds } = bindActionCreators(
    commitmentSlice.actions,
    dispatch
  );
  const { changeMainGoalValidator } = bindActionCreators(
    validatorSlice.actions,
    dispatch
  );

  const [mainGoalList, setMainGoalList] = useState(mainGoalsListNew);
  const [secondaryGoalIds, setSecondaryGoalIds] = useState([]); // ID listing for secondaryGoals
  const [selectedMainGoal, setSelectedMainGoal] = useState(undefined);

  useEffect(() => {
    /* If categoryIds array is not empty, find and set secondary and agenda goals from it. Used while editing the commitment */
    if (commitment.categoryIds && commitment.categoryIds.length > 1) {
      const mainGoalsFromCategoryIds = mainGoalList
        .filter(mappedGoal =>
          commitment.categoryIds.includes(mappedGoal.categoryId)
        )
        .map(mappedGoal => mappedGoal.categoryId);
      if (mainGoalsFromCategoryIds && mainGoalsFromCategoryIds.length === 1) {
        setSelectedMainGoal(mainGoalsFromCategoryIds[0]);
      }
      const secondaryGoalsFromCategoryIds = mainGoalList
        .filter(mappedGoal =>
          commitment.categoryIds.includes(mappedGoal.asSecondaryGoal)
        )
        .map(mappedGoal => mappedGoal.asSecondaryGoal);
      setSecondaryGoalIds([
        ...mainGoalsFromCategoryIds,
        ...secondaryGoalsFromCategoryIds
      ]);
    }
    changeMainGoalValidator(true);
  }, []);

  /* Concats content of multiple arrays into one and assigns it to the commitment as categoryIds */
  useEffect(() => {
    const ids = [
      ...new Set([
        mainGoal,
        ...secondaryGoalIds,
        ...fetchedCommitment.commitmentData.categoryIds
      ])
    ].filter(Number);

    setCategoryIds(ids);
  }, [secondaryGoalIds]);

  /* Changes the active status of mainGoals based on whether their IDs are on the list or not */
  useEffect(() => {
    setMainGoalList(
      mainGoalList.map(mappedGoal =>
        secondaryGoalIds.includes(mappedGoal.asSecondaryGoal) ||
        secondaryGoalIds.includes(mappedGoal.categoryId) ||
        mappedGoal.categoryId === mainGoal ||
        mappedGoal.categoryId === selectedMainGoal
          ? { ...mappedGoal, active: true }
          : { ...mappedGoal, active: false }
      )
    );
  }, [secondaryGoalIds, selectedMainGoal]);

  /* Changes 'active' status of a goal if it is not the main goal */
  const modifySecondaryGoals = goal => {
    if (goal.categoryId === mainGoal) {
      return;
    }

    if (secondaryGoalIds.includes(goal.asSecondaryGoal)) {
      setSecondaryGoalIds(
        secondaryGoalIds.filter(
          mappedGoal => mappedGoal !== goal.asSecondaryGoal
        )
      );
    } else {
      setSecondaryGoalIds(secondaryGoalIds.concat(goal.asSecondaryGoal));
    }
  };

  return (
    <div className="border-bottom">
      <AgendaHeader language={language} />

      <MainGoalsContainer
        mainGoalList={mainGoalList}
        modifySecondaryGoals={modifySecondaryGoals}
        language={language}
      />
    </div>
  );
};

export default AgendaContainer;

const AgendaHeader = ({ language }) => {
  return (
    <div>
      <h3>
        {translate({
          textKey: "sit.commitmentCreation.sustainableGoals",
          languageOverride: language
        })}
      </h3>
      <div className="margin-top-bot">
        <p
          className="p"
          dangerouslySetInnerHTML={{
            __html: translate({
              textKey: "sit.commitmentCreation.agendaHeader.871646",
              languageOverride: language
            })
          }}
        />
      </div>
    </div>
  );
};

const MainGoalsContainer = ({
  mainGoalList,
  modifySecondaryGoals,
  language
}) => {
  return (
    <div className="section">
      <div className="flex-row items-center margin-top-bot">
        <div className="w300">
          <p className="p">
            {translate({
              textKey: "sit.commitmentCreation.selectSecondaryGoals.871646",
              languageOverride: language
            })}
          </p>
          <Tooltip
            textKey="sit.commitment.chooseOtherInfo.871646"
            languageOverride={language}
          />
        </div>
      </div>

      {/* Displays main goals */}
      <div className="flex-row-wrap space-between row-margin">
        {mainGoalList.map(goal => (
          <SelectableGoal
            key={goal.categoryId}
            src={goal.srcImg}
            goal={goal}
            size={100}
            changeActiveState={modifySecondaryGoals}
            isMainGoal
            language={language}
            showHelper={language === "fi_FI"}
          />
        ))}
      </div>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/components/circularEconomyCommitment/agenda/AgendaContainer.jsx